'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { debounce } from 'lodash';

import TextInput from '@/components/FormFields/TextInput';
import cn from '@/helpers/utils/classNames';
import { Add, Remove } from '@mui/icons-material';

type ProductQuantityProps = {
  quantity?: number;
  onChange?: (quantity: number) => void;
  className?: string;
  limit: number;
  minValue: number;
};

export default function ProductQuantity({ onChange, quantity, className, limit, minValue }: ProductQuantityProps) {
  const [count, setCount] = useState<number | string>(1);

  const handleIncreaseQuantity = () => {
    const updatedQuantity = +count + 1;

    if (limit && updatedQuantity <= limit) {
      setCount(() => updatedQuantity);
      debouncedUpdate(updatedQuantity, count as number, quantity ?? 1);
    }
  };

  const handleDecreaseQuantity = () => {
    const updatedQuantity = +count - 1;

    if (minValue && updatedQuantity >= minValue) {
      setCount(() => updatedQuantity);
      debouncedUpdate(updatedQuantity, count as number, quantity ?? 1);
    }
  };

  const handleUpdateQuantity = useCallback(
    (updatedQuantity: number, currentStateCount: number, currentCartCount: number) => {
      let valueLimit = limit ?? 25;
      const isQuantityInRange = updatedQuantity && +updatedQuantity >= 1 && +updatedQuantity <= valueLimit;
      if (isQuantityInRange && onChange) {
        onChange(updatedQuantity);
      } else if (+updatedQuantity > valueLimit && currentStateCount !== currentCartCount && onChange) {
        onChange(currentStateCount);
      }
    },
    [onChange, limit]
  );

  const debouncedUpdate = useMemo(() => debounce(handleUpdateQuantity, 1000), [handleUpdateQuantity]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valueLimit = limit ?? 25;
    const newValue = e.target.value.trim() === '' ? '' : parseInt(e.target.value, 10);
    const isQuantityInRange = newValue && +newValue >= 1 && +newValue <= valueLimit;
    const hasEmptyValue = !newValue && newValue !== 0;

    if (hasEmptyValue || isQuantityInRange) {
      setCount(newValue);
    }

    debouncedUpdate(newValue as number, count as number, quantity ?? 1);
  };

  const handleBlur = () => {
    if (!count && count !== 0) setCount(quantity ?? 1);
  };

  const handlerKey = (event: any) => {
    if (event.key === 'e' || event.key === 'E' || event.key === '-' || event.key === '.' || event.key === '+') {
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (quantity) setCount(quantity);
  }, [quantity]);

  return (
    <div className="flex flex-row items-center gap-2">
      <Remove
        className={cn('cursor-pointer text-xl', { 'cursor-not-allowed text-grayscale-60': +count <= 1 })}
        onClick={handleDecreaseQuantity}
        // onClick={() => {
        //   if (+count <= 1) return;
        //   setCount(c => +c - 1);
        //   onChange && onChange(+count - 1);
        // }}
      />
      <TextInput
        type="number"
        variant="boxed"
        className={cn('h-[36px] w-[46px] text-center', className)}
        value={count || ''}
        onChange={handleChange}
        onKeyDown={handlerKey}
        onBlur={handleBlur}
        // onChange={event => {
        //   const value = Number(event.target.value);
        //   if (value === count) return;
        //   setCount(value);
        //   value && onChange && onChange(value);
        // }}
      />
      <Add
        className="cursor-pointer text-xl"
        onClick={handleIncreaseQuantity}
        // onClick={() => {
        //   setCount(c => +c + 1);
        //   onChange && onChange(+count + 1);
        // }}
      />
    </div>
  );
}
